import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { TEAM } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, UserPlus, Shield, Database, RefreshCw, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ROLE_TINT = {
  tenant_admin: "bg-forest-mid text-white border-forest-dark",
  contract_manager: "bg-teal-50 text-teal-800 border-teal-200",
  supervisor: "bg-blue-50 text-blue-800 border-blue-200",
  field_crew: "bg-amber-50 text-amber-900 border-amber-200",
  finance_reviewer: "bg-indigo-50 text-indigo-800 border-indigo-200",
  client_rep: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "field_crew" });
  const [saving, setSaving] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoMode, setDemoMode] = useState("reset");
  const [demoScale, setDemoScale] = useState("1");
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDemo = async () => {
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/demo/reset", { mode: demoMode, scale: Number(demoScale) }, { timeout: 180000 });
      setDemoResult(data.counts);
      toast.success(`Demo data ${demoMode === "anonymise" ? "anonymised" : "rebuilt"} — ${data.counts.work_orders} work orders, ${data.counts.score_periods} scores`, { duration: 8000 });
      setDemoOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Demo action failed"));
    } finally {
      setDemoBusy(false);
    }
  };

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/team/users"), api.get("/team/roles")]);
    setUsers(u.data);
    setRoles(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/team/users", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "field_crew" });
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/team/users/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/team/users/${id}`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5" data-testid={TEAM.root}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-forest-dark">Team & Roles</h1>
          <p className="text-sm text-muted-foreground">Invite colleagues and assign what they can see and do.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid={TEAM.addBtn} className="bg-forest-mid hover:bg-forest-dark">
          <UserPlus className="mr-1.5 h-4 w-4" /> Add member
        </Button>
      </div>

      <Card className="border-sage-light">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead>
                <TableHead>Role</TableHead><TableHead>Joined</TableHead><TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={TEAM.row(u.id)}>
                  <TableCell className="font-medium">{u.name}{u.id === user.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)} disabled={u.id === user.id}>
                      <SelectTrigger className="h-8 w-[200px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                  <TableCell>
                    {u.id !== user.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 hover:text-rose-700" onClick={() => del(u.id)} data-testid={TEAM.deleteBtn(u.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="border-sage-light p-5">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-forest-dark">
          <Shield className="h-4 w-4 text-forest-mid" /> Role reference
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {roles.map((r) => (
            <span key={r.value} className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${ROLE_TINT[r.value] || ""}`}>{r.label}</span>
          ))}
        </div>
      </Card>

      {user.role === "tenant_admin" && (
        <Card className="border-sage-light p-5" data-testid={TEAM.demoCard}>
          <h3 className="mb-1 flex items-center gap-2 font-heading text-base font-semibold text-forest-dark">
            <Database className="h-4 w-4 text-forest-mid" /> Demo data
          </h3>
          <p className="mb-3 text-sm text-muted-foreground">
            Rebuild the multi-year sample history, change its size, or anonymise client &amp; staff names for sharing. Your login stays active.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Dataset size</Label>
              <Select value={demoScale} onValueChange={setDemoScale}>
                <SelectTrigger className="h-9 w-[150px]" data-testid={TEAM.demoScale}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">Lighter (½)</SelectItem>
                  <SelectItem value="1">Standard</SelectItem>
                  <SelectItem value="2">Dense (2×)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => { setDemoMode("reset"); setDemoOpen(true); }} data-testid={TEAM.demoReset}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Reset / rescale
            </Button>
            <Button variant="outline" onClick={() => { setDemoMode("anonymise"); setDemoOpen(true); }} data-testid={TEAM.demoAnon}>
              <EyeOff className="mr-1.5 h-4 w-4" /> Anonymise
            </Button>
          </div>
          {demoResult && (
            <p className="mt-3 text-xs text-forest-mid" data-testid="team-demo-result">
              Last rebuild: {demoResult.work_orders} work orders · {demoResult.score_periods} scores · {demoResult.evidence_packs} packs · {demoResult.contracts} contracts
            </p>
          )}
        </Card>
      )}

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent data-testid={TEAM.demoDialog}>
          <DialogHeader><DialogTitle className="font-heading">{demoMode === "anonymise" ? "Anonymise demo data?" : "Rebuild demo data?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {demoMode === "anonymise"
              ? "Client and staff names will be replaced with generic labels across the whole dataset. Login emails are unchanged, so everyone can still sign in."
              : "This replaces all sample projects, work orders, scores and evidence packs with a freshly generated multi-year history at the chosen size. This cannot be undone."}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoOpen(false)}>Cancel</Button>
            <Button onClick={runDemo} disabled={demoBusy} className="bg-forest-mid hover:bg-forest-dark" data-testid={TEAM.demoConfirm}>
              {demoBusy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={TEAM.dialog}>
          <DialogHeader><DialogTitle className="font-heading">Add team member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={TEAM.nameInput} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={TEAM.emailInput} /></div>
            <div className="space-y-1.5"><Label>Temporary password</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid={TEAM.passwordInput} /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid={TEAM.roleSelect}><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-forest-mid hover:bg-forest-dark" data-testid={TEAM.saveBtn}>Add member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
